import { createAsyncThunk } from "@reduxjs/toolkit";
import { InitState, QuestionBankState, QuestionBankUpload } from "@/types";
import { httpGet, httpPost, httpPostFile } from "@/lib/http";
import { serializedError } from "../helper";

export const searchQuestions = createAsyncThunk(
  "questionBank/searchQuestions",
  async (config: any, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState() as { init: InitState };
      const source = state.init.source;
      const { subject, subSubject, publisher, book, chapter } = config;

      // calculate chapterIDs
      let chapterIDs = [];
      if (!chapter) {
        let sourceItem = source.find(({ id }) => id === subject) || {};

        if (subSubject) {
          sourceItem = (sourceItem?.subSubject || []).find(({ id }: { id: string }) => id === subSubject) || {};
        }

        const publisherItem = (sourceItem.publishers || []).find(({ id }: { id: string }) => id === publisher) || {};

        const bookItem = (publisherItem.books || []).find(({ id }: { id: string }) => id === book) || {};

        chapterIDs = bookItem.chapters.map(({ chapterId }: { chapterId: string }) => chapterId);
      } else {
        chapterIDs = [chapter]
      }

      const res = await httpPost({ url: 'questions', payload: { chapterIDs }, dispatch });
      return res ? res?.data : { questions: [] };
    } catch (err: any) {
      return rejectWithValue(serializedError(err));
    }
  }
);

export const upsertQuestions = createAsyncThunk(
  "questionBank/upsert",
  async ({ questions, config }: { questions: any, config?: any }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState() as { questionBank: QuestionBankState };
      let _questions = state.questionBank.questions;

      const prevIds = (_questions || []).map(({ id }) => id);
      const updated = questions.map((ss: any) => {
        if (!prevIds.includes(ss.id)) {
          ss.id = '';
        }
        return ss;
      });

      const saved = await httpPost({ url: 'admin/questions', payload: updated, dispatch });
      if (!saved) {
        return rejectWithValue({ message: "Questions were not saved.", status: 0 });
      }

      if (config && config.subject) {
        await dispatch(searchQuestions(config));
      }
    } catch (err: any) {
      return rejectWithValue(serializedError(err));
    }
  }
);

export const universalSearch = createAsyncThunk(
  "questionBank/universalSearch",
  async (text: string, { rejectWithValue, dispatch }) => {
    try {
      const res = await httpPost({ url: 'questions', payload: { searchText: text }, dispatch });

      return res?.data;
    } catch (err: any) {
      return rejectWithValue(serializedError(err));
    }
  }
);


// Kicks off the import; the backend responds immediately with a
// PENDING/IN_PROGRESS QuestionBankUpload record instead of waiting for the
// whole file to be processed. The caller polls fetchQuestionBankUploadStatus
// with the returned id until it reaches COMPLETED/FAILED.
export const importXlsx = createAsyncThunk(
  "questionBank/importXlsx",
  async ({ file }: { file: File }, { rejectWithValue, dispatch }) => {
    try {
      const res = await httpPostFile({ url: 'admin/questionbank/excel', file, dispatch });
      if (!res?.data) {
        return rejectWithValue({ message: "Upload did not start.", status: 0 });
      }
      return res.data as QuestionBankUpload;
    } catch (err: any) {
      return rejectWithValue(serializedError(err));
    }
  }
);

export const fetchQuestionBankUploadStatus = createAsyncThunk(
  "questionBank/fetchQuestionBankUploadStatus",
  async (uploadId: string, { rejectWithValue, dispatch }) => {
    try {
      const res = await httpGet({ url: `admin/questionbank/excel/upload/${uploadId}`, dispatch });
      if (!res?.data) {
        return rejectWithValue({ message: "Could not fetch upload status.", status: 0 });
      }
      return res.data as QuestionBankUpload;
    } catch (err: any) {
      return rejectWithValue(serializedError(err));
    }
  }
);

export const downloadQuestionBankUploadReport = createAsyncThunk(
  "questionBank/downloadQuestionBankUploadReport",
  async ({ uploadId, reportFileName }: { uploadId: string; reportFileName: string }, { rejectWithValue, dispatch }) => {
    try {
      const res = await httpGet({
        url: `admin/questionbank/excel/upload/${uploadId}/report`,
        dispatch,
        responseType: "blob",
      });

      if (!res?.data) {
        throw new Error("No report received");
      }

      const blob = new Blob([res.data], { type: "application/octet-stream" });
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = reportFileName || "question-bank-upload-report.xlsx";

      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(downloadUrl);

      return true;
    } catch (err: any) {
      return rejectWithValue(serializedError(err));
    }
  }
);

export const fetchQuestionBank = createAsyncThunk(
  "questionBank/fetchQuestionBank",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const res = await httpGet({
        url: "admin/questionbank/excel",
        dispatch,
        responseType: "blob",
      });

      if (!res?.data) {
        throw new Error("No file received");
      }

      const blob = new Blob(
        [res.data],
        {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }
      );

      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = "questionbanks.xlsx";

      document.body.appendChild(a);

      a.click();

      a.remove();

      window.URL.revokeObjectURL(downloadUrl);

      return true;
    } catch (err: any) {
      return rejectWithValue(serializedError(err));
    }
  }
);
